import React, { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router-dom';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  GridLegacy as Grid,
  IconButton,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import OfferBreadcrumbs from '~/components/Common/Elements/OfferBreadcrumbs';
import { AccordionExpandedProvider } from '~/components/Common/Forms/OfferForm/AccordionExpandedContext';
import OfferForm from '~/components/Common/Forms/OfferForm/OfferForm';
import GenerateContentModal from '~/components/Common/GenerateContentModal';

import { OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_HOTEL, OFFER_TYPE_RENTAL } from '~/consts/offerTypes';

import { getOfferContent, getOfferPrompt, initiateOfferContentGeneration } from '~/services/AccommodationService';
import OffersService from '~/services/OffersService';

import { getHotelUniquePropertyDetails, getHotelUniquePropertyIds } from '~/utils/hotelUtils';
import { reportError } from '~/utils/reportError';

import ContentLocalizationForm from './ContentLocalization/ContentLocalizationForm';
import CopyOfferModal from './CopyOfferModal';
import OfferGeneratedContentView from './OfferGeneratedContentView';

function OfferEditContainer() {
  const { id_offer: offerId } = useParams();
  const history = useHistory();

  const [state, setState] = useState({
    isCopyModalVisible: false,
    offerDetails: {},
    offerSchema: {},
    offerLocalization: {},
    offerTemplates: {},
    propertyDetails: {},
    isReady: false,
    confirmedCopyOptions: [],
    currentTab: 'general',
    offerContent: {},
    prefilledContent: {},
    isGenerateContentModalVisible: false,
    isOfferInAccommodationService: false,
    offerPrompt: {},
    useWebSearch: false,
    originalOfferDetails: null,
  });

  const fetchData = () => {
    Promise.all([
      OffersService.getOfferSchema(offerId),
      OffersService.getOffer(offerId, { retain_dynamic_tags: true }),
      OffersService.getOfferLocalization(offerId),
      OffersService.getOfferTemplates(),
    ])
      .then(async (data) => {
        const offerSchema = data[0].patch.body.schema;
        delete offerSchema.properties['surcharge_paid_direct_to_vendor'];
        const originalOfferDetails = data[1].result;
        const uniqueHotelIds = getHotelUniquePropertyIds(originalOfferDetails);
        const propertyDetails = await getHotelUniquePropertyDetails(originalOfferDetails);
        setState((prevState) => ({
          ...prevState,
          offerDetails: data[1].result,
          offerLocalization: data[2],
          offerTemplates: data[3].filter((template) => template.status === 'approved'),
          offerSchema,
          propertyDetails,
          isReady: true,
          showTooManyPropertiesError: uniqueHotelIds.length > 1,
          originalOfferDetails,
        }));
      })
      .catch((error) => {
        reportError(error);
      });
    getOfferContent({ offerId })
      .then((data) => {
        setState((prevState) => ({
          ...prevState,
          offerContent: data.result,
          isOfferInAccommodationService: true,
        }));
      })
      .catch((error) => {
        reportError(error);
      });
    getOfferPrompt()
      .then((data) => {
        setState((prevState) => ({
          ...prevState,
          offerPrompt: {
            template: data.result?.templates?.write || '',
            examples: data.result?.examples || [],
            variables: {
              abstain: data.result?.variables?.abstain || '',
              voice: data.result?.variables?.voice || '',
            },
          },
        }));
      })
      .catch((error) => {
        reportError(error);
      });
  };

  const setOfferPrompt = (offerPrompt) => {
    setState((prevState) => ({
      ...prevState,
      offerPrompt,
    }));
  };

  const showCopyModal = () => {
    setState((prevState) => ({
      ...prevState,
      isCopyModalVisible: true,
    }));
  };

  const showGenerateContentModal = () => {
    setState((prevState) => ({
      ...prevState,
      isGenerateContentModalVisible: true,
    }));
  };

  const hideGenerateContentModal = () => {
    setState((prevState) => ({
      ...prevState,
      isGenerateContentModalVisible: false,
    }));
  };

  const copyOfferData = (copiedOfferData, selectedCopyOptions) => {
    setState((prevState) => {
      const newOfferDetails = Object.assign({}, prevState.offerDetails, copiedOfferData);
      return {
        ...prevState,
        offerDetails: newOfferDetails,
        isReady: true,
        confirmedCopyOptions: selectedCopyOptions,
      };
    });
    hideCopyModal();
  };

  const onAddImage = (cloudinaryId, idOffer) => {
    return OffersService.createImage({ id_cloudinary_external: cloudinaryId }, idOffer).then((newImage) => {
      newImage.id = newImage.id_image;
      return newImage;
    });
  };

  const hideCopyModal = () => {
    setState((prevState) => ({
      ...prevState,
      isCopyModalVisible: false,
    }));
  };

  const setAsNotReady = () => {
    setState((prevState) => ({
      ...prevState,
      isReady: false,
    }));
  };

  const handleContentLocalizationChange = (fields) => {
    setState((prevState) => ({
      ...prevState,
      offerLocalization: {
        ...prevState.offerLocalization,
        localizationStrings: fields,
      },
    }));
  };

  const getLocalizedContentTabExtra = () => {
    if (state.offerDetails.type !== OFFER_TYPE_HOTEL) return '';

    const numberOfFields = state.offerLocalization?.localizationStrings?.length;

    if (!numberOfFields) return `(0)`;

    return `(${numberOfFields})`;
  };

  const handleTabChange = (_, newValue) => {
    setState((prevState) => ({
      ...prevState,
      currentTab: newValue,
    }));
  };

  const handlePrefilledContentChange = (prefilledContent) => {
    setState((prevState) => ({
      ...prevState,
      prefilledContent,
      currentTab: 'general',
    }));
  };

  const setUseWebSearch = (useWebSearch) => {
    setState((prevState) => ({
      ...prevState,
      useWebSearch,
    }));
  };

  const initiateContentGeneration = (params) => {
    return initiateOfferContentGeneration({
      offerId: state.offerDetails.id_salesforce_external,
      ...params,
    });
  };

  const fetchContent = async () => {
    const response = await getOfferContent({ offerId: state.offerDetails.id_salesforce_external });
    if (response.result) {
      setState((prevState) => ({
        ...prevState,
        offerContent: response.result,
      }));
    } else {
      reportError(`Failed to fetch offer content: ${JSON.stringify(response)}`);
    }
  };

  const hasGeneratedContent = () => {
    return state.offerContent?.generatedContents?.length > 0;
  };

  useEffect(() => {
    fetchData();
    const unlisten = history.listen((location) => {
      if (location.hash && state.currentTab !== 'general') {
        setState((prevState) => ({
          ...prevState,
          currentTab: 'general',
        }));
      }
    });

    return () => {
      unlisten();
    };
  }, [offerId]);

  return (
    <Container className="offer-edit" maxWidth="xl">
      <Helmet>
        <title>Offers | {state.offerDetails?.name || offerId} | Edit Offer</title>
      </Helmet>
      <OfferBreadcrumbs offerId={offerId} currentPage="Edit" />
      <Box mb={1}>
        <Stack mt={5} mb={1} direction="row" justifyContent="space-between">
          <Typography variant="h3" component="h1" sx={{ fontWeight: 500 }}>
            Edit Offer
          </Typography>
          <IconButton onClick={showCopyModal} size="large" color="primary" aria-label="Copy Offer">
            <ContentCopyIcon />
          </IconButton>
        </Stack>
        <Divider />
      </Box>
      <Grid container spacing={2}>
        <Grid item md={2}>
          <Box
            mt={8}
            sx={{
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
            p={1}
          >
            <Tabs
              orientation="vertical"
              variant="scrollable"
              aria-label="Vertical tabs example"
              sx={{ border: 0 }}
              value={false}
            >
              <Tab key="general-settings" label="General Settings" component={Link} href="#general-settings" />
              <Tab key="offer-fields" label="Offer Page" component={Link} href="#offer-fields" />
              <Tab key="tags-and-marketing" label="Tags & Marketing" component={Link} href="#tags-and-marketing" />
              <Tab key="offer-settings" label="Offer Settings" component={Link} href="#offer-settings" />
              {state.isReady && state.offerDetails?.type === OFFER_TYPE_HOTEL && (
                <Tab key="payment-schedules" label="Payment Schedules" component={Link} href="#payment-schedules" />
              )}
              {state.isReady && state.offerDetails?.type === OFFER_TYPE_HOTEL && (
                <Tab
                  key="dynamic-payment-schedules"
                  label="Dynamic Payment Schedules"
                  component={Link}
                  href="#dynamic-payment-schedules"
                />
              )}
              {state.isReady &&
                state.offerDetails?.type !== OFFER_TYPE_BUNDLE_AND_SAVE &&
                state.offerDetails?.type !== OFFER_TYPE_RENTAL && (
                  <Tab key="flights" label="Flights" component={Link} href="#flights" />
                )}
            </Tabs>
          </Box>
        </Grid>
        <Grid item md={10}>
          <Tabs orientation="horizontal" value={state.currentTab} onChange={handleTabChange}>
            <Tab key="general" label="Default Content" value="general" />
            <Tab
              key="content-localization"
              label={`Localised Content ${getLocalizedContentTabExtra()}`}
              value="content-localization"
            />
            <Tab key="generated-content" label="Generated Content" value="generated-content" />
          </Tabs>
          <Box
            sx={{
              display: state.currentTab === 'general' ? 'block' : 'none',
            }}
          >
            {!state.isReady && (
              <Stack mt={3} alignItems="center">
                <CircularProgress />
              </Stack>
            )}
            {state.isReady && (
              <Box mt={3}>
                <AccordionExpandedProvider>
                  <OfferForm
                    offer={state.offerDetails}
                    schema={state.offerSchema}
                    templates={state.offerTemplates}
                    confirmedCopyOptions={state.confirmedCopyOptions}
                    onAddImage={onAddImage}
                    originalOfferDetails={state.originalOfferDetails}
                    contentLocalization={state.offerLocalization?.localizationStrings || []}
                    generatorContext={
                      state.offerDetails && {
                        name: state.offerDetails.name,
                        id: state.offerDetails.id_salesforce_external,
                        offerType: state.offerDetails.type,
                        description: state.offerDetails.description,
                        location: state.offerDetails.location,
                        model: 'offer',
                        getting_there: state.offerDetails.getting_there,
                      }
                    }
                    propertyContext={
                      state.propertyDetails && {
                        description: state.propertyDetails.content_description,
                        additional_description: state.propertyDetails.additional_description,
                        highlights: state.propertyDetails.highlights_description,
                        getting_there: state.propertyDetails.locations_description,
                        facilities: state.propertyDetails.facilities_description,
                        fine_print: state.propertyDetails.fine_print,
                      }
                    }
                    showTooManyPropertiesError={state.showTooManyPropertiesError}
                    prefilledContent={state.prefilledContent}
                  />
                </AccordionExpandedProvider>
                <CopyOfferModal
                  isCopyModalVisible={state.isCopyModalVisible}
                  setAsNotReady={setAsNotReady}
                  hideCopyModal={hideCopyModal}
                  copyOfferData={copyOfferData}
                  currentOfferStatus={state.offerDetails.status}
                  originalOfferDetails={state.originalOfferDetails}
                />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: state.currentTab === 'content-localization' ? 'block' : 'none',
            }}
          >
            {!state.isReady && (
              <Stack mt={3} alignItems="center">
                <CircularProgress />
              </Stack>
            )}
            {state.offerDetails.type === OFFER_TYPE_HOTEL && state.isReady ? (
              <ContentLocalizationForm
                offer={state.offerDetails}
                fields={state.offerLocalization?.localizationStrings || []}
                onChange={handleContentLocalizationChange}
              />
            ) : (
              <p>Only for Lux Exclusive Hotels</p>
            )}
          </Box>
          <Box
            sx={{
              display: state.currentTab === 'generated-content' ? 'block' : 'none',
            }}
          >
            {state.isReady && (
              <>
                <Stack direction="row" justifyContent="space-between" display="flex" mt={2}>
                  <Box sx={{ flex: 1 }}>
                    {!state.isOfferInAccommodationService && (
                      <Typography color="warning.main">
                        This offer is not in Accommodation Service, so is unavailable for content generation
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={showGenerateContentModal}
                      disabled={!state.isOfferInAccommodationService}
                    >
                      {hasGeneratedContent() ? 'Regenerate Content' : 'Generate Content'}
                    </Button>
                  </Box>
                </Stack>
                {state.isGenerateContentModalVisible && (
                  <GenerateContentModal
                    onClose={hideGenerateContentModal}
                    prompt={state.offerPrompt}
                    setPrompt={setOfferPrompt}
                    initiateContentGeneration={initiateContentGeneration}
                    fetchContent={fetchContent}
                    includeOfferInGeneration={hasGeneratedContent()}
                    setUseWebSearch={setUseWebSearch}
                    useWebSearch={state.useWebSearch}
                  />
                )}
                {state.offerContent?.generatedContents?.length > 0 && (
                  <OfferGeneratedContentView
                    content={state.offerContent.generatedContents}
                    applyContent={handlePrefilledContentChange}
                    setOfferPrompt={setOfferPrompt}
                  />
                )}
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}

OfferEditContainer.propTypes = {
  // Add any necessary prop types here
};

export default OfferEditContainer;
